import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveServerCart, syncCartAfterLogin } from "../features/cartSlice";

const stableStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

export default function CartSyncer() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state?.userAuth?.token) || null;
  const rawItems = useSelector((state) => state?.cart?.items);
  const items = useMemo(() => (Array.isArray(rawItems) ? rawItems : []), [rawItems]);

  const itemsKey = useMemo(() => stableStringify(items.map((it) => ({
    id: it?.id,
    quantity: it?.quantity,
    sku_id: it?.sku_id,
    product_id: it?.product_id,
    selected_shipping: it?.selected_shipping,
    selected_shipping_meta: it?.selected_shipping_meta,
    logistics_service_name: it?.logistics_service_name,
  }))), [items]);

  const didSyncOnLoginRef = useRef(false);
  const syncInFlightRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    if (!token) {
      didSyncOnLoginRef.current = false;
      syncInFlightRef.current = false;
      return;
    }
    if (didSyncOnLoginRef.current) return;
    syncInFlightRef.current = true;
    dispatch(syncCartAfterLogin())
      .finally(() => {
        didSyncOnLoginRef.current = true;
        syncInFlightRef.current = false;
      });
  }, [token, dispatch]);

  useEffect(() => {
    if (!token) return;
    if (syncInFlightRef.current) return;
    if (!didSyncOnLoginRef.current) return;

    // debounce save to server while logged in
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      dispatch(saveServerCart({ items }));
    }, 600);

    return () => clearTimeout(saveTimerRef.current);
  }, [token, itemsKey, items, dispatch]);

  return null;
}
